import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Combobox, type ComboboxOption } from '../../../../ui/Combobox';

export interface PickedItem {
  id: string;
  label: string;
  /** kolor klienta (dane) */
  color?: string | null;
  /** np. poziom dostępu (Select) obok nazwy */
  trailing?: ReactNode;
  description?: string | null;
}

/** Kropka koloru klienta (dane, nie token). */
export function ClientColorDot({ color }: { color?: string | null }) {
  return (
    <span
      aria-hidden
      className="inline-block size-2 shrink-0 rounded-full bg-line-strong"
      style={color ? { backgroundColor: color } : undefined}
    />
  );
}

/**
 * Wybór wielu rekordów: lista wybranych (z „x”) + Combobox „Dodaj…” (Figma „Zaproś osobę”: Udostępnij klientów).
 * `items` z opcją `trailing` = lista wierszy (np. projekt + poziom dostępu); bez = plakietki w jednej linii.
 */
export function ResourcePicker({
  items,
  options,
  onAdd,
  onRemove,
  placeholder,
  emptyText = 'Brak wyników',
  layout = 'chips',
  disabled = false,
  'aria-label': ariaLabel,
}: {
  items: PickedItem[];
  /** opcje do dodania (bez już wybranych) */
  options: ComboboxOption[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  placeholder: string;
  emptyText?: string;
  layout?: 'chips' | 'rows';
  disabled?: boolean;
  'aria-label': string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.length > 0 &&
        (layout === 'chips' ? (
          <ul className="flex flex-wrap gap-1.5" aria-label={ariaLabel}>
            {items.map((item) => (
              <li
                key={item.id}
                className="inline-flex h-7 items-center gap-1.5 rounded-sm bg-elevated pr-1 pl-2 text-caption font-medium text-fg"
              >
                <ClientColorDot color={item.color} />
                <span className="max-w-48 truncate">{item.label}</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRemove(item.id)}
                  aria-label={`Usuń: ${item.label}`}
                  className="inline-flex size-5 items-center justify-center rounded-xs text-fg-muted hover:bg-line hover:text-fg focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50"
                >
                  <X size={12} strokeWidth={2} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="border-t border-line" aria-label={ariaLabel}>
            {items.map((item) => (
              <li key={item.id} className="flex min-h-12 items-center gap-3 border-b border-line py-1.5">
                <ClientColorDot color={item.color} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body-s text-fg">{item.label}</span>
                  {item.description && (
                    <span className="block truncate text-caption text-fg-muted">{item.description}</span>
                  )}
                </span>
                {item.trailing}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRemove(item.id)}
                  aria-label={`Usuń: ${item.label}`}
                  className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50"
                >
                  <X size={16} strokeWidth={1.75} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ))}
      <Combobox
        value={null}
        onValueChange={onAdd}
        options={options}
        placeholder={placeholder}
        emptyText={emptyText}
        disabled={disabled}
        aria-label={placeholder}
      />
    </div>
  );
}
